import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { RootState } from '../../../stateStore'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import clientInvoiceReportApi from '../../../middleware/api/Invoicing/ClientInvoiceReport/clientInvoiceReportApi'
import {
  ClientInvoiceReportState,
  ClientStatus,
  ClientsProjectInvoicesAuditProps,
  GetClientInvoiceAudit,
  result,
  InvoiceAuditProps,
  ProjectAuditProps,
  clients,
  clientResults,
  inVoiceSummary,
} from '../../../types/Invoicing/ClientInvoiceReport/ClientInvoiceReportTypes'

const getClientInvoiceAudit = createAsyncThunk(
  'invoiceClientReport/getClientInvoiceAudit',
  async (props: InvoiceAuditProps, thunkApi) => {
    try {
      return await clientInvoiceReportApi.getClientInvoiceAudit(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getClientsProjectsAudit = createAsyncThunk(
  'invoiceClientReport/getClientsProjectsAudit',
  async (props: ProjectAuditProps, thunkApi) => {
    try {
      return await clientInvoiceReportApi.getClientsProjectsAudit(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getClientsProjectInvoicesAudit = createAsyncThunk(
  'invoiceClientReport/getClientsProjectInvoicesAudit',
  async (props: ClientsProjectInvoicesAuditProps, thunkApi) => {
    try {
      return await clientInvoiceReportApi.getClientsProjectInvoicesAudit(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getInvoiceSummary = createAsyncThunk(
  'invoiceClientReport/getInvoiceSummary',
  async (invoiceId: number, thunkApi) => {
    try {
      return await clientInvoiceReportApi.getInvoiceSummary(invoiceId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getClients = createAsyncThunk(
  'invoiceClientReport/getClients',
  async (_, thunkApi) => {
    try {
      return await clientInvoiceReportApi.getClients()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const initialInvoiceClientReportState: ClientInvoiceReportState = {
  clientInvoiceAudit: {} as GetClientInvoiceAudit,
  clients: [],
  isLoading: ApiLoadingState.idle,
  selectedClientStatus: ClientStatus.all,
  ProjectInvoiceDetails: [],
  result: [],
  inVoiceSummary: {} as inVoiceSummary,
}
const invoiceClientReportSlice = createSlice({
  name: 'invoiceClientReport',
  initialState: initialInvoiceClientReportState,
  reducers: {
    changeSelectedClientStatus: (state, action) => {
      state.selectedClientStatus = action.payload as ClientStatus
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getClientInvoiceAudit.fulfilled, (state, action) => {
        state.clientInvoiceAudit = action.payload
      })
      .addCase(getClients.fulfilled, (state, action) => {
        state.clients = action.payload
      })
      .addCase(getClientsProjectsAudit.fulfilled, (state, action) => {
        state.result = action.payload.result
      })
      .addCase(getClientsProjectInvoicesAudit.fulfilled, (state, action) => {
        state.ProjectInvoiceDetails = action.payload.result
      })
      .addCase(getInvoiceSummary.fulfilled, (state, action) => {
        state.inVoiceSummary = action.payload
      })
      .addMatcher(
        isAnyOf(
          getClientInvoiceAudit.pending,
          getClients.pending,
          getClientsProjectsAudit.pending,
          getClientsProjectInvoicesAudit.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
  },
})
const invoiceClientReportThunk = {
  getClients,
  getClientInvoiceAudit,
  getClientsProjectsAudit,
  getClientsProjectInvoicesAudit,
  getInvoiceSummary,
}
const isLoading = (state: RootState): LoadingState =>
  state.clientInvoiceReport.isLoading

const invoiceAuditDetails = (state: RootState): GetClientInvoiceAudit =>
  state.clientInvoiceReport.clientInvoiceAudit

const clientsData = (state: RootState): clients[] =>
  state.clientInvoiceReport.clients

const selectedClientStatus = (state: RootState): ClientStatus =>
  state.clientInvoiceReport.selectedClientStatus

const getResult = (state: RootState): result[] =>
  state.clientInvoiceReport.result

const getProjectClientResult = (state: RootState): clientResults[] =>
  state.clientInvoiceReport.ProjectInvoiceDetails

const getInVoiceSummary = (state: RootState): inVoiceSummary =>
  state.clientInvoiceReport.inVoiceSummary

const invoiceClientReportSelectors = {
  isLoading,
  invoiceAuditDetails,
  clientsData,
  selectedClientStatus,
  getResult,
  getProjectClientResult,
  getInVoiceSummary,
}
export const clientInvoiceReportService = {
  ...invoiceClientReportThunk,
  actions: invoiceClientReportSlice.actions,
  selectors: invoiceClientReportSelectors,
}
export default invoiceClientReportSlice.reducer
